import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Checkbox,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as CustomUnCheckedIcon } from "../../assets/images/checkbox-unchecked-icon.svg";
import { ReactComponent as CustomCheckedIcon } from "../../assets/images/checkbox-checked-icon.svg";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useEffect } from "react";

function SelectIconComponent(props) {
  return <ExpandMoreIcon {...props} />;
}

CFormSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  sx: PropTypes.object,
  infoTooltip: PropTypes.string,
  forceShrink: PropTypes.bool,
};

export function CFormSelect({
  name,
  label,
  options,
  placeholder,
  helperText,
  formControlClass,
  handleChange,
  disabled,
  multiple,
  infoTooltip,
  forceShrink,
  ...other
}) {
  const { control, setValue, watch } = useFormContext();

  // Filter out empty, null, or undefined options
  const filteredOptions = options?.filter((option) => 
    option && 
    option.id !== null && 
    option.id !== undefined && 
    option.id !== "" && 
    option.value !== null && 
    option.value !== undefined && 
    option.value !== ""
  ) || [];

  // Watch the current field value
  const currentValue = watch(name);

  // Set default value to empty string if no value is set
  useEffect(() => {
    if (!currentValue && !disabled) {
      setValue(name, "");
      
      // Call handleChange if provided
      if (handleChange) {
        const mockEvent = { target: { value: "" } };
        handleChange(mockEvent, "");
      }
    }
  }, [currentValue, disabled, name, setValue, handleChange]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          className={`custom-form-select w-full ${formControlClass}`}
        >
          {label && (
            <div className="flex items-center gap-2 mb-1">
              <InputLabel
                id={name}
                shrink={
                  forceShrink !== undefined
                    ? forceShrink
                    : field?.value
                    ? true
                    : false
                }
                className="!mb-0"
              >
                {label}
              </InputLabel>
              {infoTooltip && (
                <Tooltip
                  title={infoTooltip}
                  arrow
                  placement="top"
                  className="text-xs leading-xs font-semibold"
                  sx={{
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#e5f3fa",
                      fontWeight: "600",
                    },
                  }}
                >
                  <div className="cursor-pointer">
                    <InfoIcon className="w-5 h-5 text-blue-500" />
                  </div>
                </Tooltip>
              )}
            </div>
          )}
          <Select
            {...field}
            multiple={multiple}
            labelId={name}
            IconComponent={SelectIconComponent}
            sx={{
              "& .MuiSelect-select": {
                paddingRight: "2.75rem !important",
              },
            }}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value); // Update the field value with the current selection

              if (multiple) {
                handleChange && handleChange(e, value); // Pass the array of selected values
              } else {
                // const selectedItem = options.find(
                //   (item) => item.value === value
                // );
                // handleChange && handleChange(e, selectedItem?.label);
                handleChange && handleChange(e, value);
              }
            }}
            displayEmpty
            // value={disabled ? "" : field.value}
            value={field.value || ""}
            disabled={disabled}
            className="custom-select !font-[Poppins]  lg:!text-sm lg:!leading-sm !text-xs !leading-xs  !w-full  2xl:!h-[50px] !text-blue-700 !font-medium"
            MenuProps={{
              classes: { paper: "custom-select-popup" }, // Add a custom class for popover styling
            }}
          >
            <MenuItem
              value=""
              className="font-normal 2xl:!text-base 2xl:!leading-base !text-sm !leading-sm !px-3 !py-2 hover:!bg-blue-100"
            >
              {placeholder || "Select"}
            </MenuItem>

            {filteredOptions?.map((item) => (
              <MenuItem
                key={item.id}
                value={item.id}
                disabled={item.disabled}
                className="font-normal 2xl:!text-base 2xl:!leading-base !text-sm !leading-sm !px-3 !py-2 hover:!bg-blue-100"
              >
                {multiple && (
                  <Checkbox
                    checked={field.value?.includes(item.value)}
                    icon={<CustomUnCheckedIcon />}
                    checkedIcon={<CustomCheckedIcon />}
                  />
                )}
                {item.value}
              </MenuItem>
            ))}
          </Select>
          {(!!error || helperText) && (
            <FormHelperText error={!!error}>
              {error ? error.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
